import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';
import { OperatorFunction } from 'rxjs';
import { PopperUtils } from '../../../utils/popper-utils';

@Component({
  selector: 'app-typeahead-search-input',
  templateUrl: './typeahead-search-input.component.html',
  styleUrls: ['./typeahead-search-input.component.scss'],
  standalone: true,
  imports: [NgbTypeahead],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadSearchInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypeaheadSearchInputComponent implements ControlValueAccessor {
  @Input() search!: OperatorFunction<string, readonly any[]>;
  @Input() inputFormatter!: (item: any) => string;
  @Input() resultFormatter: ((item: any) => string) | undefined;
  @Input() resultTemplate: TemplateRef<any> | undefined;
  @Input() placeholder: string = '';
  @Input() error: boolean = false;
  @Input() controlId: string | null = null;
  @Input() editable: boolean = false;

  @Output() selectItem = new EventEmitter<NgbTypeaheadSelectItemEvent>();

  @ViewChild('typeaheadInput', { static: true }) private typeaheadInstance!: NgbTypeahead;
  @ViewChild('defaultResultTemplate') private defaultResultTemplate!: TemplateRef<any>;

  private readonly cdr = inject(ChangeDetectorRef);

  public disabled: boolean = false;
  public isFocused: boolean = false;

  public popperOptions = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [...(options.modifiers ?? []), PopperUtils.sameWidth]
  });

  public onFocus(): void {
    this.isFocused = true;
    this.cdr.markForCheck();
  }

  public onBlur(): void {
    this.isFocused = false;
    this.cdr.markForCheck();
  }

  public get activeResultTemplate(): TemplateRef<any> {
    return this.resultTemplate ?? this.defaultResultTemplate;
  }

  writeValue(value: any): void {
    const formatted = value !== null && this.inputFormatter ? this.inputFormatter(value) : value ?? '';
    this.typeaheadInstance?.writeValue(formatted);
  }

  registerOnChange(fn: any): void {
    this.typeaheadInstance?.registerOnChange(fn);
  }

  registerOnTouched(fn: any): void {
    this.typeaheadInstance?.registerOnTouched(fn);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
